/**
 * Shared state data for child support calculator pages.
 * Single source of truth for state names, codes, URL slugs, and SEO descriptions.
 */

export interface StateInfo {
  name: string;
  code: string;
  slug: string;
  /** Whether this state has a custom calculator implementation */
  hasCustomCalculator: boolean;
  /** Guideline model used by this state */
  model: 'income_shares' | 'percentage_of_income' | 'hybrid';
  /** SEO meta description for this state's calculator page */
  description: string;
}

/** States with custom calculator implementations */
const CUSTOM_STATES = new Set([
  'AK', 'CA', 'CT', 'DE', 'FL', 'GA', 'HI', 'IL', 'MI', 'MS', 'MT',
  'ND', 'NJ', 'NV', 'NY', 'NC', 'OH', 'PA', 'TX', 'WI',
]);

function toSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function getModel(code: string): StateInfo['model'] {
  // Percentage-of-obligor-income states: TX (Fam. Code ch. 154),
  // MS (§43-19-101), NV (NAC 425), WI (DCF 150), AK (R. 90.3), ND (75-02-04.1)
  if (['TX', 'MS', 'NV', 'WI', 'AK', 'ND'].includes(code)) return 'percentage_of_income';
  // CA uses the §4055 K-factor formula; DE/HI/MT use the Melson formula
  if (['CA', 'DE', 'HI', 'MT'].includes(code)) return 'hybrid';
  // All other jurisdictions (incl. IL since 2017 and AR since 2020) use income shares
  return 'income_shares';
}

function getDescription(name: string, _code: string, hasCustom: boolean): string {
  if (hasCustom) {
    return `Free ${name} child support calculator using official ${name} state guidelines. Estimate monthly payments based on income, custody split, and number of children.`;
  }
  return `Free ${name} child support calculator using the income shares model. Estimate monthly payments based on both parents' income, custody arrangement, and number of children.`;
}

const STATE_ENTRIES: { name: string; code: string }[] = [
  { name: 'Alabama', code: 'AL' },
  { name: 'Alaska', code: 'AK' },
  { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' },
  { name: 'California', code: 'CA' },
  { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' },
  { name: 'Delaware', code: 'DE' },
  { name: 'District of Columbia', code: 'DC' },
  { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' },
  { name: 'Hawaii', code: 'HI' },
  { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' },
  { name: 'Indiana', code: 'IN' },
  { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' },
  { name: 'Kentucky', code: 'KY' },
  { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' },
  { name: 'Maryland', code: 'MD' },
  { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' },
  { name: 'Minnesota', code: 'MN' },
  { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' },
  { name: 'Montana', code: 'MT' },
  { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' },
  { name: 'New Hampshire', code: 'NH' },
  { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' },
  { name: 'New York', code: 'NY' },
  { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' },
  { name: 'Ohio', code: 'OH' },
  { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' },
  { name: 'Pennsylvania', code: 'PA' },
  { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' },
  { name: 'South Dakota', code: 'SD' },
  { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' },
  { name: 'Utah', code: 'UT' },
  { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' },
  { name: 'Washington', code: 'WA' },
  { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' },
  { name: 'Wyoming', code: 'WY' },
];

export const ALL_STATES: StateInfo[] = STATE_ENTRIES.map(({ name, code }) => {
  const hasCustomCalculator = CUSTOM_STATES.has(code);
  return {
    name,
    code,
    slug: toSlug(name),
    hasCustomCalculator,
    model: getModel(code),
    description: getDescription(name, code, hasCustomCalculator),
  };
});

/** Lookup by URL slug (e.g. "new-york" -> StateInfo) */
export const STATE_BY_SLUG: Record<string, StateInfo> = Object.fromEntries(
  ALL_STATES.map(s => [s.slug, s]),
);

/** Lookup by state code (e.g. "NY" -> StateInfo) */
export const STATE_BY_CODE: Record<string, StateInfo> = Object.fromEntries(
  ALL_STATES.map(s => [s.code, s]),
);
